import type { TensionBucket } from '@tunasong/music-lib'
import cn from 'classnames'
import type { FC } from 'react'
import React from 'react'
import styles from './simple-chord.module.css'
import { getTensionStripeHeightPx, tensionStripeClass } from './tension-stripe.js'

export interface TensionStripeProps {
  /** Analyzer bucket — drives BOTH the hue class and the height (one lookup). */
  bucket: TensionBucket
  /** Muted (low-confidence) treatment — desaturated + dashed (visual spec §9). */
  lowConfidence?: boolean
}

/**
 * The chord-chip under-stripe (Joni visual spec §2–§3, §9). Presentational and
 * Redux-free — gating + bucket resolution live in the caller (`SimpleChord`).
 * Dual-encoded from one bucket: hue via the CSS-module class, height via inline
 * style (the SC 1.4.1 redundant channel). `aria-hidden` — the meaning is
 * mirrored into the chip's aria-label, so the stripe is decorative-redundant.
 */
export const TensionStripe: FC<TensionStripeProps> = props => {
  const { bucket, lowConfidence = false } = props
  // Resolve the logical bucket/muted tokens against the CSS module.
  const moduleClasses = tensionStripeClass(bucket, lowConfidence)
    .split(' ')
    .map(key => styles[key])
  return (
    <i
      aria-hidden="true"
      data-tension-bucket={bucket}
      data-tension-muted={lowConfidence ? 'true' : undefined}
      className={cn(styles.tensionStripe, ...moduleClasses)}
      style={{ height: `${getTensionStripeHeightPx(bucket)}px` }}
    />
  )
}
